import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Edit, Trash2, Building2, X, AlertCircle, Users, Eye, Search, Crown, UserCheck, User } from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../utils/auth';
import Pagination, { usePagination } from './Pagination';
import StyledDropdown from './StyledDropdown';
import DeleteImpactModal from './DeleteImpactModal';

const GroupManagement = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'Active',
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewingGroup, setViewingGroup] = useState(null);
  const [memberSearch, setMemberSearch] = useState('');

  // Fetch groups
  const fetchGroups = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${getApiUrl()}/api/groups`, {
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (result.success) {
        setGroups(result.data);
      } else {
        setError(result.message || 'Failed to fetch groups');
      }
    } catch (err) {
      console.error('Fetch groups error:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const response = await fetch(`${getApiUrl()}/api/groups`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setFormData({ name: '', description: '', status: 'Active' });
        setShowCreateModal(false);
        fetchGroups();
      } else {
        setError(result.message || 'Failed to create group');
      }
    } catch (err) {
      console.error('Create group error:', err);
      setError('Unable to connect to server');
    }
  };

  const handleEditClick = (group) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      description: group.description || '',
      status: group.status,
    });
    setShowEditModal(true);
  };

  const handleUpdateGroup = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const response = await fetch(`${getApiUrl()}/api/groups/${editingGroup.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          status: formData.status,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setShowEditModal(false);
        setEditingGroup(null);
        setFormData({ name: '', description: '', status: 'Active' });
        fetchGroups();
      } else {
        setError(result.message || 'Failed to update group');
      }
    } catch (err) {
      console.error('Update group error:', err);
      setError('Unable to connect to server');
    }
  };

  const handleDeleteGroup = async (id) => {
    try {
      setError('');
      const response = await fetch(`${getApiUrl()}/api/groups/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (result.success) {
        setDeleteConfirm(null);
        fetchGroups();
      } else {
        setError(result.message || 'Failed to delete group');
        setDeleteConfirm(null);
      }
    } catch (err) {
      console.error('Delete group error:', err);
      setError('Unable to connect to server');
      setDeleteConfirm(null);
    }
  };

  const getMemberCount = (group) => (
    group.memberCount ?? ((group.groupLeader ? 1 : 0) + (group.teamLeaders || 0) + (group.agents || 0))
  );

  const getRoleIcon = (role) => {
    if (role === 'Group Leader') return <Crown className="w-4 h-4 text-amber-500" />;
    if (role === 'Team Leader') return <UserCheck className="w-4 h-4 text-primary-500" />;
    return <User className="w-4 h-4 text-gray-400" />;
  };

  const getRoleBadge = (role) => {
    const styles = {
      'Group Leader': 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300',
      'Team Leader': 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300',
      Agent: 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-dark-text',
    };
    return styles[role] || 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-dark-text';
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const filteredMembers = useMemo(() => {
    const members = viewingGroup?.members || [];
    const q = memberSearch.trim().toLowerCase();
    if (!q) return members;
    return members.filter((member) => (
      member.name?.toLowerCase().includes(q) ||
      member.email?.toLowerCase().includes(q) ||
      member.accountId?.toLowerCase().includes(q) ||
      member.role?.toLowerCase().includes(q)
    ));
  }, [viewingGroup, memberSearch]);

  const getStatusBadge = (status) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        status === 'Active' 
          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
      }`}>
        {status}
      </span>
    );
  };

  // Recent 3 groups for cards (by createdAt desc)
  const recentGroups = useMemo(() => {
    return [...groups]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 3);
  }, [groups]);

  // Pagination for table
  const {
    currentPage,
    paginatedItems: paginatedGroups,
    totalItems,
    handlePageChange,
  } = usePagination(groups, 10);

  const renderModal = (isEdit = false) => {
    const modalTitle = isEdit ? 'Edit Group' : 'Create New Group';
    const submitLabel = isEdit ? 'Update Group' : 'Create Group';
    const onSubmit = isEdit ? handleUpdateGroup : handleCreateGroup;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="neo-modal p-6 w-full max-w-md transition-colors duration-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text">{modalTitle}</h3>
            <button
              onClick={() => {
                if (isEdit) {
                  setShowEditModal(false);
                  setEditingGroup(null);
                } else {
                  setShowCreateModal(false);
                }
                setFormData({ name: '', description: '', status: 'Active' });
                setError('');
              }}
              className="text-gray-400 dark:text-dark-muted hover:text-gray-600 dark:hover:text-dark-text"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={onSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Group Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 neo-field dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  placeholder="e.g., Group A"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 neo-field dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  placeholder="Brief description of the group"
                  rows="3"
                />
              </div>

              {isEdit && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                    Status
                  </label>
                  <StyledDropdown
                    options={[
                      { value: 'Active', label: 'Active' },
                      { value: 'Inactive', label: 'Inactive' }
                    ]}
                    value={formData.status}
                    onChange={(val) => setFormData({...formData, status: val})}
                    placeholder="Select Status"
                    className="w-full"
                  />
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  if (isEdit) {
                    setShowEditModal(false);
                    setEditingGroup(null);
                  } else {
                    setShowCreateModal(false);
                  }
                  setFormData({ name: '', description: '', status: 'Active' });
                  setError('');
                }}
                className="px-4 py-2 text-gray-700 dark:text-dark-text neo-btn transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 neo-btn-primary transition-colors duration-200"
              >
                {submitLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 dark:text-primary-400 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-600 dark:text-dark-muted">Loading groups...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Error Message */}
      {error && !showCreateModal && !showEditModal && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Action Button */}
      <div className="flex items-center justify-end mb-6">
        <button
          onClick={() => {
            setShowCreateModal(true);
            setFormData({ name: '', description: '', status: 'Active' });
            setError('');
          }}
          className="neo-btn-primary px-4 py-2 text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Create Group</span>
        </button>
      </div>

      {/* Groups Grid - recent 3 only */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6 mb-6 lg:mb-8">
        {recentGroups.map((group) => (
          <div key={group.id} className="neo-raised p-4 lg:p-6 transition-all duration-200">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-dark-text">{group.name}</h3>
                  {getStatusBadge(group.status)}
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handleEditClick(group)}
                  className="p-1 text-gray-400 dark:text-dark-muted hover:text-gray-600 dark:hover:text-primary-400 rounded transition-colors"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setDeleteConfirm(group.id)}
                  className="p-1 text-gray-400 dark:text-dark-muted hover:text-red-600 dark:hover:text-red-400 rounded transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <p className="text-gray-600 dark:text-dark-muted text-sm mb-4">{group.description || 'No description'}</p>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-dark-muted">Group Leader:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-dark-text">
                  {group.groupLeader?.name || 'Not assigned'}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-dark-muted">Team Leaders:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-dark-text">{group.teamLeaders || 0}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-dark-muted">Agents:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-dark-text">{group.agents || 0}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-dark-muted">Members:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-dark-text">{getMemberCount(group)}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-dark-muted">Created:</span>
                <span className="text-sm text-gray-900 dark:text-dark-text">
                  {new Date(group.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => { setViewingGroup(group); setMemberSearch(''); }}
              className="mt-4 w-full px-3 py-2 neo-btn text-sm font-medium text-primary-700 dark:text-primary-300 flex items-center justify-center gap-2"
            >
              <Users className="w-4 h-4" />
              View members
            </button>
          </div>
        ))}
      </div>

      {/* Groups Table */}
      <div className="neo-raised overflow-hidden transition-colors duration-200">
        <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-200 dark:border-dark-border">
          <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text">All Groups</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
            <thead className="bg-gray-50 dark:bg-slate-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Group
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Group Leader
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Members
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Team Structure
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200/70 dark:divide-dark-border">
              {paginatedGroups.map((group) => (
                <tr key={group.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center mr-3">
                        <Building2 className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-dark-text">{group.name}</div>
                        <div className="text-sm text-gray-500 dark:text-dark-muted">{group.description || 'No description'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-dark-text">
                      {group.groupLeader?.name || (
                        <span className="text-gray-400 dark:text-dark-muted italic">Not assigned</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => { setViewingGroup(group); setMemberSearch(''); }}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300"
                    >
                      <Users className="w-4 h-4" />
                      {getMemberCount(group)} {getMemberCount(group) === 1 ? 'member' : 'members'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-dark-muted">
                      <span>{group.teamLeaders || 0} Team Leaders</span>
                      <span>•</span>
                      <span>{group.agents || 0} Agents</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(group.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => { setViewingGroup(group); setMemberSearch(''); }}
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 transition-colors inline-flex items-center gap-1"
                      >
                        <Eye className="w-4 h-4" />
                        Members
                      </button>
                      <button
                        onClick={() => handleEditClick(group)}
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(group.id)}
                        className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={10}
          onPageChange={handlePageChange}
        />
      </div>

      {viewingGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="neo-modal p-6 w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text">{viewingGroup.name}</h3>
                <p className="text-sm text-gray-500 dark:text-dark-muted mt-1">
                  {getMemberCount(viewingGroup)} {getMemberCount(viewingGroup) === 1 ? 'member' : 'members'}
                  {viewingGroup.groupLeader?.name ? ` • Leader: ${viewingGroup.groupLeader.name}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setViewingGroup(null); setMemberSearch(''); }}
                className="text-gray-400 dark:text-dark-muted hover:text-gray-600 dark:hover:text-dark-text"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="relative mb-4">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-muted pointer-events-none" />
              <input
                type="text"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                placeholder="Search members..."
                className="w-full pl-9 pr-3 py-2 neo-field dark:text-dark-text text-sm"
              />
            </div>

            <div className="overflow-y-auto space-y-2 flex-1 pr-1">
              {filteredMembers.length > 0 ? (
                filteredMembers.map((member) => (
                  <div key={member.id} className="neo-raised p-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-sm font-semibold text-primary-700 dark:text-primary-300 flex-shrink-0">
                      {getInitials(member.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-gray-900 dark:text-dark-text truncate">{member.name}</p>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadge(member.role)}`}>
                          {getRoleIcon(member.role)}
                          {member.role}
                        </span>
                        {member.status && member.status !== 'Active' && (
                          <span className="text-xs text-gray-500 dark:text-dark-muted">{member.status}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-dark-muted truncate">
                        {[member.accountId, member.email].filter(Boolean).join(' • ') || 'No contact details'}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-10 text-sm text-gray-500 dark:text-dark-muted">
                  {viewingGroup.members?.length ? 'No members match your search' : 'No members in this group yet'}
                </div>
              )}
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => { setViewingGroup(null); setMemberSearch(''); }}
                className="px-4 py-2 neo-btn text-gray-700 dark:text-dark-text"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && renderModal(false)}

      {/* Edit Modal */}
      {showEditModal && renderModal(true)}

      <DeleteImpactModal
        open={!!deleteConfirm}
        entityType="group"
        entityId={deleteConfirm}
        title="Delete group"
        onClose={() => setDeleteConfirm(null)}
        onDeleted={() => { setDeleteConfirm(null); fetchGroups(); }}
      />
    </div>
  );
};

export default GroupManagement;
